import API_BASE_URL from '../../config/api';
import apiClient from '../../services/apiClient';
import { ladeEigeneWahl } from '../../pages/einstellungen/benachrichtigungen';

// Server-Anbindung der Kundenseite (BAU-HILFE.md 3, PF-308-1): Wege unter /api/hilfe über apiClient (Cookie, gleicher
// Origin). Fehlerform des Servers { code, error } (backend/src/support/fehler.ts). Spätere Aufträge ergänzen hier ihre Wege.
export const HILFE_API = '/api/hilfe';

/** Antwort von GET /api/hilfe/uebersicht, genau wie backend/src/support/uebersicht.ts (HilfeUebersicht). */
export type HilfeUebersicht = {
  assistent: boolean;
  zaehler: { offeneTickets: number; ungelesen: number; aktiveStoerungen: number; offeneWuensche: number; neuigkeiten: number };
  firma: boolean;
  firmenName: string | null;
  einzelkonto: boolean;
};

/** Was die Leiste braucht; geladen beim Öffnen der Seite, nicht laufend. */
export async function ladeUebersicht(signal?: AbortSignal): Promise<HilfeUebersicht> {
  const { data } = await apiClient.get<HilfeUebersicht>(`${HILFE_API}/uebersicht`, { signal });
  return data;
}

/** Der Code einer Fehlerantwort ({ code, error }), sonst null (kein Netz, Abbruch, fremde Form). */
export function hilfeFehlerCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** Der ganze Körper einer Fehlerantwort (error, dazu etwa minuten und art bei 429), sonst null. */
export function hilfeFehlerDaten(error: unknown): Record<string, unknown> | null {
  const daten = (error as { response?: { data?: unknown } } | null)?.response?.data;
  return daten && typeof daten === 'object' ? daten as Record<string, unknown> : null;
}

// PF-309: der Hilfe-Assistent, genau wie backend/src/support/assistent.ts (PF-303-2).
export type ChatThema = { id: string; frage: string };
export type TicketKategorie = 'FEHLER' | 'ANTWORT_FALSCH' | 'FRAGE' | 'KONTO' | 'ABRECHNUNG' | 'WUNSCH' | 'SONSTIGES';
export type TicketVorschlag = { betreff: string; kategorie: TicketKategorie; beschreibung: string };
/** Eine Nachricht des Verlaufs. Gespeichert trägt sie den Ticket-Vorschlag nur als Merkmal; den Vorschlag selbst liefert die
 *  Antwort auf das Senden bzw. GET /assistent/entwurf. */
export type ChatNachricht = { id: string; rolle: 'PERSON' | 'ASSISTENT'; text: string; themen: ChatThema[]; ticketVorschlag: boolean; rechtsfrage: boolean; zeit: string;
  /** PF-329: Bewertung der Person an einer Antwort des Assistenten (1 hilfreich, -1 nicht hilfreich, null keine). */
  bewertung?: 1 | -1 | null };
export type AssistentStand = { an: true; beispiele: string[]; unterhaltung: { id: string; nachrichten: ChatNachricht[] } | null };
export type ChatEingabe = { unterhaltungId: string | null; text: string; seite?: string; fehlercodes?: string[] };
export type AssistentAntwort = {
  unterhaltungId: string;
  antwort: { id: string; text: string; themen: ChatThema[]; ticketVorschlag: TicketVorschlag | null; rechtsfrage: boolean };
  verbleibend: { stunde: number; tag: number };
};
export type ChatEntwurf = { betreff: string; kategorie: TicketKategorie; beschreibung: string; nachrichten: number };

// Der letzte Vorschlag je Unterhaltung aus den Antworten dieser Sitzung im Browser (nur im Speicher). Der Server speichert ihn
// nicht; ladeEntwurf nimmt ihn statt des Entwurfs aus der ersten Frage, wenn er da ist.
const vorschlaege = new Map<string, TicketVorschlag>();

/** GET /assistent: Beispiel-Fragen und die fortsetzbare Unterhaltung der letzten 24 Stunden. 503 ASSISTANT_OFF oder
 *  ASSISTANT_BUDGET, wenn der Assistent für dieses Konto aus ist. */
export async function ladeAssistent(signal?: AbortSignal): Promise<AssistentStand> {
  const { data } = await apiClient.get<AssistentStand>(`${HILFE_API}/assistent`, { signal });
  return data;
}

/** POST /assistent/nachrichten. */
export async function sendeFrage(eingabe: ChatEingabe): Promise<AssistentAntwort> {
  const { data } = await apiClient.post<AssistentAntwort>(`${HILFE_API}/assistent/nachrichten`, eingabe);
  if (data.antwort.ticketVorschlag) vorschlaege.set(data.unterhaltungId, data.antwort.ticketVorschlag);
  return data;
}

/** PF-332: POST /assistent/nachrichten/:id/bewertung (PF-329): „hilfreich“ (1) oder „nicht hilfreich“ (-1) für eine Antwort; eine
 *  neue Bewertung ersetzt die alte, auch wenn der Assistent gerade aus ist. */
export async function bewerteAntwort(id: string, wert: 1 | -1): Promise<{ wert: 1 | -1 }> {
  const { data } = await apiClient.post<{ wert: 1 | -1 }>(`${HILFE_API}/assistent/nachrichten/${encodeURIComponent(id)}/bewertung`, { wert });
  return data;
}

/** POST /assistent/neu: die offene Unterhaltung endet, der Verlauf ist danach leer. */
export async function neueUnterhaltung(): Promise<void> {
  await apiClient.post(`${HILFE_API}/assistent/neu`, {});
}

/** GET /assistent/entwurf (für „Ticket anlegen“, PF-310-1): Betreff, Kategorie und Beschreibung aus dem letzten Vorschlag dieser
 *  Sitzung, sonst aus der ersten Frage; nachrichten zählt der Server. 404 CONVERSATION_NOT_FOUND für fremde oder unbekannte. */
export async function ladeEntwurf(unterhaltungId: string, signal?: AbortSignal): Promise<ChatEntwurf> {
  const { data } = await apiClient.get<ChatEntwurf>(`${HILFE_API}/assistent/entwurf`, { params: { unterhaltungId }, signal });
  const vorschlag = vorschlaege.get(unterhaltungId);
  return vorschlag ? { ...data, ...vorschlag } : data;
}

// PF-310-1: Tickets der Person, genau wie backend/src/support/tickets.ts (liste, anlegen) und ticketWege.ts.
export type TicketFilter = 'offen' | 'geschlossen' | 'alle';
export type TicketZeile = {
  referenz: string; betreff: string; status: string; kategorie: string; vorschau: string; letzteVon: string; zuletzt: string;
  ungelesen: boolean; gefolgt: boolean; melder: string | null;
};
export type TicketListe = { eintraege: TicketZeile[]; mehr: boolean };
export type NeuesTicket = {
  kategorie: TicketKategorie; betreff: string; beschreibung: string; sichtbarkeit: 'FIRMA' | 'PRIVAT'; seite?: string; fehlercode?: string;
  version?: string; unterhaltungId?: string; verlaufAnhaengen?: boolean; stoerungId?: string; rechercheId?: string; rechercheAnhaengen?: boolean; anfrageId: string;
};

/** GET /tickets: eigene und gefolgte Anfragen, 20 je Seite. 400 SEARCH_TOO_BROAD bei zu vielen Kandidaten. */
export async function ladeTickets(abfrage: { filter: TicketFilter; suche: string; seite: number }, signal?: AbortSignal): Promise<TicketListe> {
  const { data } = await apiClient.get<TicketListe>(`${HILFE_API}/tickets`, { params: abfrage, signal });
  return data;
}

/** POST /tickets: ein Vorgang mit Dateien (multipart, Textteil „daten“, bis zu drei „dateien“). 201 neu, 200 bei gleicher anfrageId. */
export async function sendeTicket(daten: NeuesTicket, dateien: File[]): Promise<{ referenz: string; status: string }> {
  const form = new FormData();
  form.append('daten', JSON.stringify(daten));
  for (const datei of dateien) form.append('dateien', datei, datei.name);
  const { data } = await apiClient.post<{ referenz: string; status: string }>(`${HILFE_API}/tickets`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

// PF-310-2: eine Anfrage, genau wie backend/src/support/ticketAnsicht.ts (ohne interne Notizen und Dateien, die liefert der
// Server nie), und ihre Aktionen aus tickets.ts. Jede Aktion sendet die geladene revision (409 STALE_TICKET bei einem älteren Stand).
export type Absender = 'PERSON' | 'FIRMA' | 'SUPPORT' | 'SYSTEM' | 'VERLAUF';
export type TicketAnhang = { id: string; name: string; groesse: number; typ: string };
export type TicketNachricht = { id: string; von: Absender; eigen: boolean; name: string | null; text: string; zeit: string; anhaenge: TicketAnhang[] };
export type TicketAktionen = {
  antworten: boolean; bestaetigen: boolean; wiederOeffnen: boolean; zurueckziehen: boolean; sichtbarkeitAendern: boolean; folgen: boolean;
  neueAnfrageDazu: boolean;
};
export type TicketAnsicht = {
  referenz: string; betreff: string; status: string; kategorie: string; sichtbarkeit: 'FIRMA' | 'PRIVAT'; quelle: string; eingegangen: string;
  eigen: boolean; melder: string | null; gefolgt: boolean; nachrichten: TicketNachricht[]; wartetSeit: string | null;
  geloestAmAutomatisch: string | null; geloestAm: string | null; wiederOeffnenBis: string | null; stoerung: { id: string; titel: string } | null;
  wunsch: { referenz: string } | null; bewertung: { wert: number | null; offen: boolean } | null; aktionen: TicketAktionen; revision: number;
};

/** Der HTTP-Status einer Fehlerantwort, sonst null. */
export function hilfeFehlerStatus(error: unknown): number | null {
  const status = (error as { response?: { status?: unknown } } | null)?.response?.status;
  return typeof status === 'number' ? status : null;
}

const ticketPfad = (referenz: string) => `${HILFE_API}/tickets/${encodeURIComponent(referenz)}`;

/** GET /tickets/:referenz; fremd oder unbekannt 404 TICKET_NOT_FOUND. */
export async function ladeTicket(referenz: string): Promise<TicketAnsicht> {
  const { data } = await apiClient.get<TicketAnsicht>(ticketPfad(referenz));
  return data;
}

/** POST /tickets/:referenz/nachrichten (multipart wie sendeTicket: daten = { text, erwartetRevision }, bis zu drei dateien). */
export async function antworte(referenz: string, text: string, revision: number, dateien: File[]): Promise<{ nachricht: { id: string; zeit: string }; status: string }> {
  const form = new FormData();
  form.append('daten', JSON.stringify({ text, erwartetRevision: revision }));
  for (const datei of dateien) form.append('dateien', datei, datei.name);
  const { data } = await apiClient.post<{ nachricht: { id: string; zeit: string }; status: string }>(`${ticketPfad(referenz)}/nachrichten`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

export async function bestaetige(referenz: string, revision: number): Promise<void> {
  await apiClient.post(`${ticketPfad(referenz)}/bestaetigen`, { erwartetRevision: revision });
}

/** „Nein, das Problem besteht weiter“: bis 14 Tage nach „Gelöst“, sonst 409 TICKET_REOPEN_EXPIRED. */
export async function oeffneWieder(referenz: string, text: string, revision: number): Promise<void> {
  await apiClient.post(`${ticketPfad(referenz)}/wieder-oeffnen`, { text, erwartetRevision: revision });
}

export async function ziehZurueck(referenz: string, revision: number): Promise<void> {
  await apiClient.post(`${ticketPfad(referenz)}/zurueckziehen`, { erwartetRevision: revision });
}

/** Einmal je Anfrage; 409 RATING_EXISTS bzw. RATING_NOT_OPEN. */
export async function bewerte(referenz: string, wert: number, kommentar?: string): Promise<void> {
  await apiClient.post(`${ticketPfad(referenz)}/bewertung`, kommentar ? { wert, kommentar } : { wert });
}

export async function aendereSichtbarkeit(referenz: string, sichtbarkeit: 'FIRMA' | 'PRIVAT', revision: number): Promise<void> {
  await apiClient.patch(`${ticketPfad(referenz)}/sichtbarkeit`, { sichtbarkeit, erwartetRevision: revision });
}

/** POST /tickets/:referenz/gelesen: die Person (oder wer folgt) hat die Anfrage gesehen. */
export async function alsGelesen(referenz: string): Promise<void> {
  await apiClient.post(`${ticketPfad(referenz)}/gelesen`, {});
}

/** Adresse zum Herunterladen einer Datei; der Server sendet sie immer als Anhang (Content-Disposition: attachment). */
export const anhangAdresse = (referenz: string, id: string) => `${API_BASE_URL}${ticketPfad(referenz)}/anhaenge/${encodeURIComponent(id)}`;

/** Bekommt die Person Antworten des Supports per E-Mail? Die wirksame Wahl der Zeile hilfe.antwort aus „Eigene Wahl“ der
 *  Benachrichtigungen (GET /api/administration/me/notifications); fehlt sie oder scheitert das Laden: nein. */
export async function ladeMailWahl(signal?: AbortSignal): Promise<boolean> {
  return (await ladeMailWirksam('hilfe.antwort', signal)) === true;
}

/** Die wirksame Wahl einer Zeile der eigenen Wahl (etwa hilfe.stoerung); null, wenn die Zeile fehlt oder das Laden scheitert
 *  (etwa in einer Sicht). */
export async function ladeMailWirksam(schluessel: string, signal?: AbortSignal): Promise<boolean | null> {
  try {
    const wahl = await ladeEigeneWahl(signal);
    const zeile = wahl.gruppen.flatMap(gruppe => gruppe.ereignisse).find(eintrag => eintrag.schluessel === schluessel);
    return zeile ? zeile.wirksam : null;
  } catch {
    return null;
  }
}

// PF-311-1: Störungen, genau wie backend/src/support/stoerungen.ts und routes/publicStoerungen.ts (PF-304).
export type StoerungEintrag = {
  id: string; titel: string; status: string; auswirkung: string; bereiche: string[]; seit: string; behobenAm: string | null; abonniert: boolean;
  letzteMeldung: { zeit: string; text: string } | null;
};
export type StoerungsListe = { aktuell: StoerungEintrag[]; behoben: StoerungEintrag[] };
export type StoerungDetail = StoerungEintrag & { meldungen: { zeit: string; status: string; text: string }[] };
export type LeistenStoerung = { id: string; titel: string; status: string; auswirkung: string };

/** GET /stoerungen: aktuelle und in den letzten 30 Tagen behobene, nur veröffentlichte. */
export async function stoerungen(signal?: AbortSignal): Promise<StoerungsListe> {
  const { data } = await apiClient.get<StoerungsListe>(`${HILFE_API}/stoerungen`, { signal });
  return data;
}

/** GET /stoerungen/:id mit allen Meldungen (neueste zuerst); 404 INCIDENT_NOT_FOUND für unbekannte und unveröffentlichte. */
export async function stoerung(id: string, signal?: AbortSignal): Promise<StoerungDetail> {
  const { data } = await apiClient.get<StoerungDetail>(`${HILFE_API}/stoerungen/${encodeURIComponent(id)}`, { signal });
  return data;
}

/** PUT /stoerungen/:id/abo; abonniert bleibt true, solange ein eigenes verknüpftes Ticket besteht. */
export async function stoerungAbo(id: string, an: boolean): Promise<{ abonniert: boolean }> {
  const { data } = await apiClient.put<{ abonniert: boolean }>(`${HILFE_API}/stoerungen/${encodeURIComponent(id)}/abo`, { an });
  return data;
}

/** GET /stoerungen/leiste: die schwerste aktive Störung ab „Teilweise eingeschränkt“ oder null. */
export async function stoerungsLeiste(signal?: AbortSignal): Promise<{ stoerung: LeistenStoerung | null }> {
  const { data } = await apiClient.get<{ stoerung: LeistenStoerung | null }>(`${HILFE_API}/stoerungen/leiste`, { signal });
  return data;
}

// PF-311-2: Wünsche, genau wie backend/src/support/wuensche.ts (PF-304).
export type WunschFilter = 'oeffentlich' | 'firma' | 'meine' | 'abgestimmt';
export type WunschSortierung = 'stimmen' | 'neu' | 'geaendert';
export type WunschZeile = {
  referenz: string; titel: string; status: string; sichtbarkeit: 'OEFFENTLICH' | 'FIRMA'; moderation: string; stimmen: number; abgestimmt: boolean; folgt: boolean;
  eigen: boolean; zusammengefuehrtIn: string | null;
};
export type WunschDetailDaten = {
  referenz: string; titel: string; beschreibung: string; status: string; statusSeit: string; sichtbarkeit: 'OEFFENTLICH' | 'FIRMA'; moderation: string;
  grund: string | null; antwort: string | null; stimmen: number; abgestimmt: boolean; folgt: boolean; herkunft: { art: string; text: string };
  zusammengefuehrtIn: string | null; aktionen: { bearbeiten: boolean; loeschen: boolean; abstimmen: boolean; folgen: boolean }; revision: number;
};
const wunschPfad = (referenz: string) => `${HILFE_API}/wuensche/${encodeURIComponent(referenz)}`;

/** GET /wuensche: 20 je Seite; 400 VALIDATION bei filter=firma ohne Firma, 400 SEARCH_TOO_BROAD. */
export async function wuensche(abfrage: { filter: WunschFilter; sortierung: WunschSortierung; suche: string; seite: number }, signal?: AbortSignal):
  Promise<{ eintraege: WunschZeile[]; mehr: boolean }> {
  const { data } = await apiClient.get<{ eintraege: WunschZeile[]; mehr: boolean }>(`${HILFE_API}/wuensche`, { params: abfrage, signal });
  return data;
}

/** GET /wuensche/:referenz; 404 WISH_NOT_FOUND für fremde, unbekannte und unsichtbare. */
export async function wunsch(referenz: string, signal?: AbortSignal): Promise<WunschDetailDaten> {
  const { data } = await apiClient.get<WunschDetailDaten>(wunschPfad(referenz), { signal });
  return data;
}

/** GET /wuensche/aehnlich: bis zu drei freigegebene, offene, sichtbare Wünsche zum Titel (3 bis 120 Zeichen). */
export async function aehnlicheWuensche(titel: string, signal?: AbortSignal): Promise<{ referenz: string; titel: string; stimmen: number }[]> {
  const { data } = await apiClient.get<{ eintraege: { referenz: string; titel: string; stimmen: number }[] }>(`${HILFE_API}/wuensche/aehnlich`,
    { params: { titel }, signal });
  return data.eintraege;
}

/** POST /wuensche: 201 neu, 200 bei gleicher anfrageId; 429 WISH_RATE_LIMIT. */
export async function wunschEinreichen(daten: { titel: string; beschreibung: string; sichtbarkeit: 'OEFFENTLICH' | 'FIRMA'; anfrageId: string }):
  Promise<{ referenz: string; moderation: string }> {
  const { data } = await apiClient.post<{ referenz: string; moderation: string }>(`${HILFE_API}/wuensche`, daten);
  return data;
}

/** PATCH /wuensche/:referenz (nur eigene im Status NEU); 409 WISH_LOCKED, STALE_WISH. */
export async function wunschAendern(referenz: string, daten: { titel?: string; beschreibung?: string; erwartetRevision: number }): Promise<WunschDetailDaten> {
  const { data } = await apiClient.patch<WunschDetailDaten>(wunschPfad(referenz), daten);
  return data;
}

/** DELETE /wuensche/:referenz (eigen, NEU, ohne fremde Stimme); 409 WISH_LOCKED, WISH_HAS_VOTES. */
export async function wunschLoeschen(referenz: string): Promise<void> {
  await apiClient.delete(wunschPfad(referenz));
}

/** PUT /wuensche/:referenz/stimme; 409 WISH_CLOSED bei umgesetzten, abgelehnten und zusammengeführten. */
export async function wunschStimme(referenz: string, an: boolean): Promise<{ stimmen: number; abgestimmt: boolean }> {
  const { data } = await apiClient.put<{ stimmen: number; abgestimmt: boolean }>(`${wunschPfad(referenz)}/stimme`, { an });
  return data;
}

export async function wunschFolgen(referenz: string, an: boolean): Promise<{ folgt: boolean }> {
  const { data } = await apiClient.put<{ folgt: boolean }>(`${wunschPfad(referenz)}/folgen`, { an });
  return data;
}

// PF-312: Anfragen der Organisation, genau wie backend/src/support/firma.ts (PF-305). Antworten und Wiederöffnen gehen über die
// Wege der Person (antworte, oeffneWieder); der Server prüft dort das Schreibrecht der Firma.
export type FirmenStatusFilter = 'alle' | 'offen' | 'wartet' | 'geloest' | 'geschlossen';
export type FirmenZeitraum = '12m' | 'jahr' | 'alle';
export type FirmenPerson = { name: string; stand: 'AKTIV' | 'AUSGETRETEN' | 'GELOESCHT' };
/** Private Anfragen ohne Referenz, Betreff und Person; in einer Sicht ohne Betreff. */
export type FirmenZeile = { referenz: string | null; betreff: string | null; privat: boolean; person: FirmenPerson | null; eingegangen: string; status: string;
  gefolgt: boolean };
export type FirmenKennzahlen = { gesamt: number; offen: number; wartet: number; geloest: number };
export type FirmenListe = { kennzahlen: FirmenKennzahlen; eintraege: FirmenZeile[]; mehr: boolean };

/** GET /firma/tickets; 403 FORBIDDEN ohne members.read in der eigenen Firma, 400 SEARCH_TOO_BROAD. */
export async function firmenAnfragen(abfrage: { status: FirmenStatusFilter; suche: string; zeitraum: FirmenZeitraum; seite: number }, signal?: AbortSignal):
  Promise<FirmenListe> {
  const { data } = await apiClient.get<FirmenListe>(`${HILFE_API}/firma/tickets`, { params: abfrage, signal });
  return data;
}

/** GET /firma/tickets/:referenz: eine FIRMA-Anfrage der eigenen Firma; private, fremde und unbekannte 404. */
export async function firmenAnfrage(referenz: string): Promise<TicketAnsicht> {
  const { data } = await apiClient.get<TicketAnsicht>(`${HILFE_API}/firma/tickets/${encodeURIComponent(referenz)}`);
  return data;
}

export async function firmenAnfrageFolgen(referenz: string, an: boolean): Promise<{ folgt: boolean }> {
  const { data } = await apiClient.put<{ folgt: boolean }>(`${HILFE_API}/firma/tickets/${encodeURIComponent(referenz)}/folgen`, { an });
  return data;
}

// PF-323: Neuigkeiten und eigene Hilfe-Dokumente, genau wie backend/src/support/neuigkeiten.ts und dokumenteBetreiber.ts
// (PF-321-1). Die Einträge für die Verwaltung (zielgruppe VERWALTUNG) liefert der Server nur Konten mit den Einstellungen der Firma.
export type Neuigkeit = { id: string; titel: string; text: string; kennzeichen: 'NEU' | 'BEHOBEN'; zielgruppe: 'ALLE' | 'VERWALTUNG';
  veroeffentlichtAm: string };
export type NeuigkeitenListe = { eintraege: Neuigkeit[]; ungelesen: number };
export type BetreiberDokument = { id: string; titel: string; text: string; stichwoerter: string[]; zielgruppe: 'ALLE' | 'VERWALTUNG' };

/** GET /neuigkeiten: die sichtbaren, neueste zuerst (höchstens 200), dazu die Zahl der ungelesenen. */
export async function neuigkeiten(signal?: AbortSignal): Promise<NeuigkeitenListe> {
  const { data } = await apiClient.get<NeuigkeitenListe>(`${HILFE_API}/neuigkeiten`, { signal });
  return data;
}

/** POST /neuigkeiten/gelesen: alles bis jetzt gilt als gelesen. Nie in einer Sicht (der Server weist es dort ab). */
export async function neuigkeitenGelesen(): Promise<{ ungelesen: number }> {
  const { data } = await apiClient.post<{ ungelesen: number }>(`${HILFE_API}/neuigkeiten/gelesen`, {});
  return data;
}

/** GET /dokumente: die veröffentlichten eigenen Dokumente des Betreibers, nach Titel. */
export async function hilfeDokumente(signal?: AbortSignal): Promise<{ dokumente: BetreiberDokument[] }> {
  const { data } = await apiClient.get<{ dokumente: BetreiberDokument[] }>(`${HILFE_API}/dokumente`, { signal });
  return data;
}

// PF-321-2: die Ansprechpersonen für KI der eigenen Firma, genau wie backend/src/support/ansprechpersonen.ts.
export type KiAnsprechperson = { name: string; email: string; telefon: string | null };
/** GET /ansprechpersonen: höchstens fünf; Einzelkonto, Konto ohne Firma und Sicht bekommen eine leere Liste. */
export async function ansprechpersonen(signal?: AbortSignal): Promise<{ personen: KiAnsprechperson[] }> {
  const { data } = await apiClient.get<{ personen: KiAnsprechperson[] }>(`${HILFE_API}/ansprechpersonen`, { signal });
  return data;
}

// PF-332 (F-21): „Problem bei der Anmeldung?“ ohne Anmeldung, genau wie backend/src/routes/publicHilfe.ts (PF-330-a). JSON über
// apiClient wie die übrigen Aufrufe der Anmeldeseite; der Token steht nur im Körper, nie in einer Adresse oder einem Log.
const ANMELDEPROBLEM_API = '/api/public/hilfe/anmeldeproblem';
/** POST …/anmeldeproblem: antwortet immer gleich (202), ob es zur Adresse ein Konto gibt oder nicht; liefert den Status. */
export async function meldeAnmeldeproblem(eingabe: { email: string; beschreibung: string; anfrageId: string }): Promise<number> {
  const antwort = await apiClient.post(ANMELDEPROBLEM_API, eingabe);
  return antwort.status;
}
/** POST …/anmeldeproblem/bestaetigen { token }: 200 { referenz } oder 410 LINK_EXPIRED; nur auf einen Klick, nie beim Laden. */
export async function bestaetigeAnmeldeproblem(token: string): Promise<{ referenz: string }> {
  const { data } = await apiClient.post<{ referenz: string }>(`${ANMELDEPROBLEM_API}/bestaetigen`, { token });
  return data;
}

/** GET /api/public/stoerungen/anmeldung: ohne Anmeldung; nur Titel und Status einer Störung des Bereichs „Anmeldung“. */
export async function anmeldeStoerung(signal?: AbortSignal): Promise<{ stoerung: { titel: string; status: string } | null }> {
  const { data } = await apiClient.get<{ stoerung: { titel: string; status: string } | null }>('/api/public/stoerungen/anmeldung', { signal });
  return data;
}
